/**
 * Sweep views model — V109-5-2..5-5 (design run `v109-track5-views`, round 3 + punch list). Pure: no React.
 *
 * One vocabulary everywhere: Met / Not met / Couldn't check / Not yet tested / Not applicable; the
 * cause is one of three (your environment changed / our rules changed / GAC Pro couldn't read it).
 * Couldn't check and Preview are never counted as Met (LM-9, LM-21). Every count a view shows comes
 * from a function here, so the tests can check that the buckets add up to the rows.
 */
import { getProgramManifest, getProvider, type InfraResource, type ProgramCard, type ProgramManifest, type ProgramSweepReport, type SweepCause, type SweepCheckState, type SweepRunStamp, type SweepSummary, type UnresolvedClue } from '@aigovops/core';

export const CAUSE_LABEL: Readonly<Record<SweepCause, string>> = {
  environment: 'Your environment changed',
  rules: 'Our rules changed',
  couldnt_read: "GAC Pro couldn't read it",
};

export const STATE_LABEL: Readonly<Record<SweepCheckState, string>> = {
  met: 'Met', not_met: 'Not met', couldnt_check: "Couldn't check", not_applicable: 'Not applicable',
};

/** The program's unit: GovRAMP counts controls, FedRAMP counts KSIs (Track 1 P11). */
export function unitNoun(unit: 'control' | 'ksi', n: number): string {
  if (unit === 'ksi') return n === 1 ? 'KSI' : 'KSIs';
  return n === 1 ? 'control' : 'controls';
}
export const unitOf = (m: ProgramManifest | undefined): 'control' | 'ksi' => (m?.features.persistentValidation ? 'ksi' : 'control');

const pad = (n: number) => String(n).padStart(2, '0');
const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** "today 06:02", "yesterday 06:01", "tomorrow 06:00", "Oct 12, 06:01" (local time). */
export function clockText(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'not yet';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'not yet';
  const y = new Date(now); y.setDate(y.getDate() - 1);
  const t = new Date(now); t.setDate(t.getDate() + 1);
  if (dayKey(d) === dayKey(now)) return `today ${hhmm(d)}`;
  if (dayKey(d) === dayKey(y)) return `yesterday ${hhmm(d)}`;
  if (dayKey(d) === dayKey(t)) return `tomorrow ${hhmm(d)}`;
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${hhmm(d)}`;
}

/** A sweep older than this reads as stale (amber clock), not as current. */
export const STALE_SWEEP_HOURS = 36;

export interface Freshness { text: string; stale: boolean; partial: boolean; none: boolean }

/** The one freshness line every view states: "Swept today 06:02 · vs yesterday 06:01". */
export function freshness(run: SweepRunStamp | null | undefined, compared: SweepRunStamp | null | undefined, now: Date = new Date()): Freshness {
  if (!run) return { text: 'No sweep yet', stale: false, partial: false, none: true };
  const at = run.finished_at ?? run.started_at;
  const age = (now.getTime() - new Date(at).getTime()) / 3_600_000;
  const parts = [`Swept ${clockText(at, now)}`];
  if (run.state === 'partial' || run.state === 'failed') parts.push(run.state === 'failed' ? 'nothing could be read' : 'partial');
  parts.push(compared ? `vs ${clockText(compared.finished_at ?? compared.started_at, now)}` : 'first sweep');
  return { text: parts.join(' · '), stale: age > STALE_SWEEP_HOURS, partial: run.state !== 'complete', none: false };
}

/** "since yesterday" / "since Oct 12" / "since the last sweep". */
export function sinceText(compared: SweepRunStamp | null | undefined, now: Date = new Date()): string {
  if (!compared) return 'since the last sweep';
  const t = clockText(compared.finished_at ?? compared.started_at, now);
  if (t.startsWith('today')) return 'since earlier today';
  if (t.startsWith('yesterday')) return 'since yesterday';
  return `since ${t.split(',')[0]}`;
}

// ---- program links: the current stage, never a hard-coded key (R-c, Track 1 P5) -------------------------

/** A program card as far as the links need it (a key may come from the summary before the cards load). */
export interface CardLike { program: string; periods: ProgramCard['periods'] }
const periodsOf = (card: Pick<CardLike, 'periods'> | null | undefined) => (card?.periods ?? []).filter((p) => p.active);

/**
 * Where "What changed" lives (R-a): the program's current stage — Prepare while any active period is
 * before certification (FedRAMP's gated Operate included), Operate once every active period is
 * certified. No card → Prepare.
 */
export function currentStage(card: CardLike | null | undefined): 'prepare' | 'operate' {
  const ps = periodsOf(card);
  if (!ps.length) return 'prepare';
  if (ps.some((p) => p.stage === 'scope' || p.stage === 'prepare' || p.stage === 'audit')) return 'prepare';
  const m = card ? getProgramManifest(card.program) : undefined;
  if (m?.shell.operateGate && !ps.some((p) => p.stage === 'certified')) return 'prepare';
  return 'operate';
}

/** The program page link: `/programs/<key>/<stage>[?query][#hash]`. */
export function programHref(prefix: string, program: string, stage: string, query: Record<string, string | null | undefined> = {}, hash?: string): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v) q.set(k, v);
  const s = q.toString();
  return `${prefix}/programs/${encodeURIComponent(program)}/${stage}${s ? `?${s}` : ''}${hash ? `#${hash}` : ''}`;
}

export const changesHref = (prefix: string, card: CardLike, extra: Record<string, string | null | undefined> = {}) =>
  programHref(prefix, card.program, currentStage(card), { view: 'changes', ...extra });
export const inventoryHref = (prefix: string, program: string | null, extra: Record<string, string | null | undefined> = {}) =>
  program ? programHref(prefix, program, 'scope', { view: 'inventory', ...extra }) : `${prefix}/boundary?view=inventory${Object.entries(extra).filter(([, v]) => v).map(([k, v]) => `&${k}=${encodeURIComponent(v!)}`).join('')}`;
export const verdictsHref = (prefix: string, program: string) => programHref(prefix, program, 'scope', { view: 'verdicts' });

/**
 * "10 found · 2 clues to confirm" — the notice, the toast, Setup, the system summary and Reveal's heading
 * all say the same thing (built-UI #1): resources found, then the clues still to confirm.
 */
export function foundText(found: number, clues: number): string {
  return `${found} found${clues ? ` · ${clues} clue${clues === 1 ? '' : 's'} to confirm` : ''}`;
}

/**
 * The Report stage's Assessment Results panel anchor (Track 6's `AssessmentResults` heading id). Null would
 * unlink "Assessment results" and "KSI validation package" (built-UI #4: never a label without its file); the
 * roll-up also lists them only for a program whose manifest issues `oscal_ar_program`.
 */
export const ASSESSMENT_RESULTS_ANCHOR: string | null = 'assessment-results';

/** The program the workspace-level links open: the first sweep program in manifest order (punch #8). */
export function leadSweepProgram(summary: Pick<SweepSummary, 'programs'> | null | undefined): string | null {
  return summary?.programs[0]?.program ?? null;
}

/** The summary's Next (round-3 #5): the first program something broke in, else the lead one. */
export function nextProgramFor(summary: Pick<SweepSummary, 'programs'> | null | undefined): string | null {
  const ps = summary?.programs ?? [];
  return (ps.find((p) => p.broke > 0) ?? ps.find((p) => p.couldnt_check > 0) ?? ps[0])?.program ?? null;
}

/**
 * BU-13: the system page's Next, one band per program (never one band that mixes programs): for each
 * program in order, its top change (the first that broke, else the first it couldn't check) and only that
 * program's refs. Programs with nothing to do get no band.
 */
export interface ProgramNext<C> { program: string; change: C; refs: string[] }
export function nextByProgram<C extends { category: string; affects: ReadonlyArray<{ program: string; ref: string }> }>(programs: readonly string[], changes: readonly C[]): ProgramNext<C>[] {
  const out: ProgramNext<C>[] = [];
  for (const program of programs) {
    const mine = changes.filter((c) => c.affects.some((a) => a.program === program));
    const change = mine.find((c) => c.category === 'newly_failing') ?? mine.find((c) => c.category === 'unevaluated');
    if (change) out.push({ program, change, refs: change.affects.filter((a) => a.program === program).map((a) => a.ref) });
  }
  return out;
}

// ---- the one change statement ("↓3 since yesterday · 2 broke · 2 couldn't check · 1 newly passing") -------

export interface StatementPart { key: 'delta' | 'broke' | 'couldnt' | 'passing'; text: string; sr?: string; tone?: 'bad' | 'good' }

export function changeStatement(input: { delta: number | null; broke: number; couldnt: number; passing: number; since: string; unit: 'control' | 'ksi' }): StatementPart[] {
  const out: StatementPart[] = [];
  if (input.delta !== null && input.delta !== 0) {
    const down = input.delta < 0;
    out.push({ key: 'delta', text: `${down ? '↓' : '↑'}${Math.abs(input.delta)} ${input.since}`, sr: `${down ? 'down' : 'up'} ${Math.abs(input.delta)} ${unitNoun(input.unit, Math.abs(input.delta))} met ${input.since}`, tone: down ? 'bad' : 'good' });
  }
  if (input.broke) out.push({ key: 'broke', text: `${input.broke} broke`, tone: 'bad' });
  if (input.couldnt) out.push({ key: 'couldnt', text: `${input.couldnt} couldn't check` });
  if (input.passing) out.push({ key: 'passing', text: `${input.passing} newly passing` });
  return out;
}

// ---- the roll-up (program readiness from determinations) ----------------------------------------------------

export interface RollupCounts { met: number; not_met: number; couldnt_check: number; not_tested: number; not_applicable: number; total: number }

/** GovRAMP overview readiness (the tier being assessed); Not applicable is its own count, outside the bar. */
export function rollupFromGovramp(c: { met: number; not_met: number; no_evidence: number; couldnt_check: number; not_applicable?: number; total: number }): RollupCounts {
  return { met: c.met, not_met: c.not_met, couldnt_check: c.couldnt_check, not_tested: c.no_evidence, not_applicable: c.not_applicable ?? 0, total: c.total };
}

// ---- the program's control list (the Readiness view; built-UI #1 / #3) -----------------------------------------

/** The five lists a program's controls fall in — the roll-up's legend, in the list's order. */
export type ControlBucket = 'not_met' | 'couldnt_check' | 'not_tested' | 'met' | 'not_applicable';
export const CONTROL_BUCKETS: readonly ControlBucket[] = ['not_met', 'couldnt_check', 'not_tested', 'met', 'not_applicable'];
export const BUCKET_LABEL: Readonly<Record<ControlBucket, string>> = {
  not_met: 'Not met', couldnt_check: "Couldn't check", not_tested: 'Not yet tested', met: 'Met', not_applicable: 'Not applicable',
};
export const isBucket = (s: string | null | undefined): s is ControlBucket => !!s && (CONTROL_BUCKETS as readonly string[]).includes(s);

/** A GovRAMP overview row → its list (Couldn't check and N/A win over the engine's status). */
export function bucketOfGovramp(r: { status: string; couldnt_check?: unknown; not_applicable?: boolean }): ControlBucket {
  if (r.not_applicable) return 'not_applicable';
  if (r.status !== 'met' && r.couldnt_check) return 'couldnt_check';
  return r.status === 'met' ? 'met' : r.status === 'not_met' ? 'not_met' : 'not_tested';
}

/** A FedRAMP requirement's check result → its list (an accepted risk is still not met). */
export function bucketOfCheckState(s: string | null | undefined): ControlBucket {
  if (s === 'pass') return 'met';
  if (s === 'fail' || s === 'excepted') return 'not_met';
  if (s === 'couldnt_check') return 'couldnt_check';
  if (s === 'not_applicable') return 'not_applicable';
  return 'not_tested';
}

/** The sweep's state of a control → its list. */
export const bucketOfSweepState = (s: SweepCheckState): ControlBucket => s;

export interface ControlRowLite { requirement_id: string; ref: string; bucket: ControlBucket }

export function countBuckets(rows: readonly { bucket: ControlBucket }[]): Record<ControlBucket, number> {
  const out: Record<ControlBucket, number> = { not_met: 0, couldnt_check: 0, not_tested: 0, met: 0, not_applicable: 0 };
  for (const r of rows) out[r.bucket]++;
  return out;
}

/**
 * The views' one reconciliation (built-UI #1, the prototype's `check()`): the roll-up's counts add up to
 * the total; each list has as many rows as its count; every control the sweep reached sits in the same
 * list as the program shows it (a newly failing check's control in Not met, a Couldn't check one in
 * Couldn't check or Not met); the sweep's share never exceeds the program's. Empty = it reconciles.
 */
export function reconcileViews(input: { counts: RollupCounts; rows?: readonly ControlRowLite[] | null; report?: Pick<ProgramSweepReport, 'program' | 'checked' | 'changes'> | null }): string[] {
  const errs: string[] = [];
  const c = input.counts;
  if (c.met + c.not_met + c.couldnt_check + c.not_tested + c.not_applicable !== c.total) errs.push('counts ≠ total');
  const rows = input.rows ?? null;
  if (rows) {
    const n = countBuckets(rows);
    for (const k of CONTROL_BUCKETS) if (n[k] !== c[k]) errs.push(`${k}: ${n[k]} rows ≠ ${c[k]}`);
  }
  const r = input.report;
  if (r) {
    const byId = new Map((rows ?? []).map((x) => [x.requirement_id, x.bucket]));
    const now = r.checked.now;
    for (const k of ['met', 'not_met', 'couldnt_check', 'not_applicable'] as const) if (now[k] > c[k]) errs.push(`sweep ${k} ${now[k]} > program ${c[k]}`);
    if (rows) {
      for (const q of r.checked.requirements) {
        const b = byId.get(q.requirement_id);
        if (b && b !== bucketOfSweepState(q.state)) errs.push(`${q.ref}: sweep ${q.state} ≠ program ${b}`);
      }
      for (const ch of r.changes) {
        for (const a of ch.affects.filter((x) => x.program === r.program)) {
          const b = byId.get(a.requirement_id);
          if (!b) continue;
          if (ch.category === 'newly_failing' && b !== 'not_met') errs.push(`${a.ref}: broke but ${b}`);
          if (ch.category === 'unevaluated' && b !== 'couldnt_check' && b !== 'not_met') errs.push(`${a.ref}: couldn't check but ${b}`);
        }
      }
    }
  }
  return errs;
}

/** Framework readiness by check result (FedRAMP KSIs for the class): an accepted risk is still not met. */
export function rollupFromCheckCounts(c: { pass: number; fail: number; couldnt_check: number; excepted: number; no_evidence: number; not_applicable: number; unmapped: number; total: number }): RollupCounts {
  return { met: c.pass, not_met: c.fail + c.excepted, couldnt_check: c.couldnt_check, not_tested: c.no_evidence + c.unmapped, not_applicable: c.not_applicable, total: c.total };
}

export type RollupTone = 'off_track' | 'at_risk' | 'on_track';
export interface RollupModel {
  applicable: number;
  /** Segments of the bar (100% = applicable; N/A sits outside it). Sum = applicable. */
  segments: Array<{ key: 'met' | 'not_met' | 'couldnt_check' | 'not_tested'; n: number; label: string }>;
  toGo: number;
  status: { tone: RollupTone; text: string };
  /** met + not met + couldn't check + not yet tested + N/A = total. */
  reconciles: boolean;
}

/**
 * The status chip (#8): Off track when something broke since the last sweep; At risk when anything is
 * not met or couldn't be checked; On track only when nothing is. The chip carries no second count (#11).
 */
export function rollupStatus(c: RollupCounts, broke: number): { tone: RollupTone; text: string } {
  if (broke > 0) return { tone: 'off_track', text: 'Off track' };
  if (c.not_met > 0 || c.couldnt_check > 0) return { tone: 'at_risk', text: 'At risk' };
  return { tone: 'on_track', text: 'On track' };
}

export function rollupModel(c: RollupCounts, broke: number, unit: 'control' | 'ksi'): RollupModel {
  const applicable = c.total - c.not_applicable;
  const n = (k: number) => Math.max(0, k);
  const segments: RollupModel['segments'] = [
    { key: 'met', n: n(c.met), label: `${c.met} met` },
    { key: 'not_met', n: n(c.not_met), label: `${c.not_met} not met` },
    { key: 'couldnt_check', n: n(c.couldnt_check), label: `${c.couldnt_check} couldn't check` },
    { key: 'not_tested', n: n(c.not_tested), label: `${c.not_tested} not yet tested` },
  ];
  void unit;
  return {
    applicable,
    segments,
    toGo: Math.max(0, applicable - c.met),
    status: rollupStatus(c, broke),
    reconciles: c.met + c.not_met + c.couldnt_check + c.not_tested + c.not_applicable === c.total,
  };
}

/** The target tick's words: every applicable control met, by the date that matters (from the program's own data). */
export function targetText(m: RollupModel, label: string, by: { what: string; date: string } | null): string {
  const need = `${label} needs ${m.applicable} met`;
  if (!m.toGo) return `${need} · all met`;
  return `${need} · ${m.toGo} to go${by ? ` before ${by.what}, ${by.date}` : ''}`;
}

/** The delta beside the hero: met now − met in the sweep compared with (the sweep's share; null without one). */
export function metDelta(report: Pick<ProgramSweepReport, 'checked'> | null | undefined): number | null {
  if (!report?.checked.prev) return null;
  return report.checked.now.met - report.checked.prev.met;
}

// ---- Reveal (the inventory after a sweep) ---------------------------------------------------------------

export type RevealDisposition = 'in_boundary' | 'external' | 'inherited' | 'excluded' | 'unresolved';
export const REVEAL_FILTERS: ReadonlyArray<[RevealDisposition, string]> = [
  ['in_boundary', 'In boundary'], ['external', 'External service'], ['inherited', 'Inherited'], ['excluded', 'Excluded'], ['unresolved', 'Unresolved'],
];
export const DISPOSITION_TEXT: Readonly<Record<RevealDisposition, string>> = {
  in_boundary: 'In boundary', external: 'External service', inherited: 'Inherited', excluded: 'Excluded', unresolved: 'Unresolved',
};

export interface RevealRow {
  resource: InfraResource;
  /** This program's disposition (the view's program first); `others` when another program differs. */
  disposition: RevealDisposition;
  others: Array<{ program: string; disposition: RevealDisposition }>;
  isNew: boolean;
}
export interface RevealGroup { key: string; label: string; rows: RevealRow[] }
export interface RevealModel {
  clues: UnresolvedClue[];
  groups: RevealGroup[];
  counts: Record<RevealDisposition, number> & { all: number };
  /** Disposition buckets + clues = all (the filter chips add up). */
  reconciles: boolean;
}

const providerName = (key: string | null | undefined) => (key ? getProvider(key)?.name ?? key : 'Other');

export function revealModel(resources: readonly InfraResource[], clues: readonly UnresolvedClue[], program: string | null, newSince: string | null): RevealModel {
  const live = resources.filter((r) => !r.gone_at);
  const groups = new Map<string, RevealGroup>();
  const counts = { in_boundary: 0, external: 0, inherited: 0, excluded: 0, unresolved: 0, all: 0 };
  for (const r of live) {
    const mine = program ? r.dispositions.find((d) => d.program === program)?.disposition ?? r.disposition : r.disposition;
    const others = r.dispositions.filter((d) => d.program !== program && d.disposition !== mine).map((d) => ({ program: d.program, disposition: d.disposition }));
    const key = r.item?.provider_key ?? 'other';
    const g = groups.get(key) ?? { key, label: providerName(r.item?.provider_key), rows: [] };
    g.rows.push({ resource: r, disposition: mine, others, isNew: !!newSince && r.first_seen_at > newSince });
    groups.set(key, g);
    counts[mine]++;
  }
  counts.unresolved += clues.length;
  counts.all = live.length + clues.length;
  const sorted = [...groups.values()].sort((a, b) => b.rows.length - a.rows.length || a.label.localeCompare(b.label));
  for (const g of sorted) g.rows.sort((a, b) => a.resource.name.localeCompare(b.resource.name));
  const sum = counts.in_boundary + counts.external + counts.inherited + counts.excluded + counts.unresolved;
  return { clues: [...clues], groups: sorted, counts, reconciles: sum === counts.all };
}

/** Rows a filter shows (null = all). */
export function filterReveal(m: RevealModel, f: RevealDisposition | null): { clues: UnresolvedClue[]; groups: RevealGroup[]; shown: number } {
  const clues = f === null || f === 'unresolved' ? m.clues : [];
  const groups = m.groups.map((g) => ({ ...g, rows: f === null ? g.rows : g.rows.filter((r) => r.disposition === f) })).filter((g) => g.rows.length);
  return { clues, groups, shown: clues.length + groups.reduce((s, g) => s + g.rows.length, 0) };
}

/** Plain words for where a clue was found ("Code · web/src/analytics.ts"); never a raw identifier dump. */
export function clueWhere(c: Pick<UnresolvedClue, 'source_kind' | 'detail'>): string {
  const SRC: Record<string, string> = {
    repo_scan: 'Code', connector_resource: 'A connection', sbom_component: 'A software bill of materials', oauth_app: 'A sign-in app',
    connector_scope: 'A connection', gcp_asset_inventory: 'Google Cloud', cloudflare_collector: 'Cloudflare',
  };
  const path = typeof c.detail.path === 'string' ? c.detail.path : typeof c.detail.file === 'string' ? c.detail.file : null;
  return [SRC[c.source_kind] ?? 'A source', path].filter(Boolean).join(' · ');
}
